import { StaticPageLayout } from '../layouts/static-page-layout';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  FileText, 
  Users, 
  Shield, 
  BookOpen, 
  AlertCircle, 
  Clock, 
  Mail, 
  Phone,
  CheckCircle,
  XCircle,
  Scale
} from 'lucide-react';
import { useEffect } from 'react';

export function TermsOfServicePage() {
  // Set page title
  useEffect(() => {
    document.title = 'Terms of Service - AceHorizon';
  }, []);

  const termsSection = [
    {
      icon: CheckCircle,
      title: "Acceptance of Terms",
      gradient: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20",
      content: {
        text: "By creating an account or using Ace Horizon, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our platform."
      }
    },
    {
      icon: Users,
      title: "User Accounts & Eligibility",
      gradient: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20",
      content: {
        items: [
          "You must provide accurate and complete information when creating an account",
          "You are responsible for maintaining the security of your account credentials",
          "Our platform is designed for 9th class students and above (ages starting from 14) with parental supervision",
          "Parents/guardians must approve and supervise accounts for users under 18",
          "One person may not create multiple accounts without authorization"
        ]
      }
    },
    {
      icon: Shield,
      title: "Acceptable Use Policy",
      gradient: "from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20",
      content: {
        intro: "You agree to use our platform responsibly and not to:",
        items: [
          "Use the platform for any illegal or unauthorized purpose",
          "Share inappropriate, offensive, or harmful content",
          "Attempt to hack, disrupt, or compromise our systems",
          "Share your account credentials with others",
          "Use automated tools to access our platform without permission",
          "Interfere with other users' learning experience"
        ]
      }
    },
    {
      icon: BookOpen,
      title: "Educational Content & Services",
      gradient: "from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20",
      content: {
        items: [
          "Our educational content is for personal, non-commercial use only",
          "We strive for accuracy but do not guarantee error-free content",
          "Content may be updated or modified without prior notice",
          "Use our platform as a supplement to, not replacement for, formal education",
          "AI-generated responses are for educational guidance and should be verified"
        ]
      }
    },
    {
      icon: Scale,
      title: "Intellectual Property Rights",
      gradient: "from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20",
      content: {
        text: "All content, features, and functionality on Ace Horizon, including but not limited to text, graphics, logos, software, and educational materials, are owned by us or our licensors and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without explicit permission."
      }
    }
  ];

  const prohibitedActivities = [
    "Using the platform for commercial purposes without authorization",
    "Attempting to reverse engineer or copy our AI algorithms",
    "Uploading malicious code or viruses",
    "Impersonating other users or providing false information",
    "Violating any applicable local, state, or federal laws"
  ];

  return (
    <StaticPageLayout>
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          
          {/* Hero Section */}
          <div className="text-center py-5 sm:py-12">
            <div className="max-w-4xl mx-auto px-2 sm:px-4">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 dark:from-cyan-400 dark:via-blue-500 dark:to-purple-500 bg-clip-text text-transparent leading-tight px-2">
                Terms of Service
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed px-2">
                Welcome to <span className="text-primary font-semibold">Ace Horizon</span>. These Terms of Service govern your use of our 
                AI-powered learning platform. By accessing or using our services, you agree to be 
                <span className="text-primary font-semibold"> bound by these terms</span>.
              </p>
              <div className="text-sm text-muted-foreground">
                <Clock className="inline-block w-4 h-4 mr-2" />
                Last Updated: {new Date().toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Terms Sections */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent text-center">
              Terms & Conditions
            </h2>
            
            <div className="space-y-8">
              {termsSection.map((section, index) => {
                const IconComponent = section.icon;
                return (
                  <Card key={index} className={`border-0 bg-gradient-to-br ${section.gradient} transition-all duration-300 hover:shadow-lg`}>
                    <CardHeader>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                          <IconComponent className="h-6 w-6 text-white" />
                        </div>
                        <CardTitle className="text-xl sm:text-2xl font-bold">
                          {index + 1}. {section.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {section.content.intro && (
                        <p className="text-base sm:text-lg mb-4 text-muted-foreground">
                          {section.content.intro}
                        </p>
                      )}
                      {section.content.text && (
                        <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                          {section.content.text}
                        </p>
                      )}
                      {section.content.items && (
                        <ul className="space-y-3">
                          {section.content.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-3 text-base sm:text-lg text-muted-foreground">
                              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Prohibited Activities Section */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 dark:from-violet-400 dark:via-purple-400 dark:to-fuchsia-400 bg-clip-text text-transparent text-center">
              Prohibited Activities
            </h2>
            <Card className="border-0 bg-gradient-to-br from-red-50 to-pink-100 dark:from-red-900/20 dark:to-pink-800/20">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl font-bold flex items-center gap-3">
                  <XCircle className="h-6 w-6 text-red-600" />
                  Activities That Are Not Permitted
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base sm:text-lg text-muted-foreground mb-4">
                  In addition to the acceptable use policy above, the following activities are strictly prohibited:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {prohibitedActivities.map((activity, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 bg-white/50 dark:bg-black/20 rounded-lg">
                      <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-base sm:text-lg text-muted-foreground">{activity}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Legal & Liability Section */}
          <div className="mb-16 space-y-8">
            {/* Limitation of Liability */}
            <Card className="border-0 bg-gradient-to-br from-yellow-50 to-orange-100 dark:from-yellow-900/20 dark:to-orange-800/20">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl font-bold flex items-center gap-3">
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                  Limitation of Liability
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                  Ace Horizon is provided "as is" without warranties of any kind. We shall not be liable for any 
                  indirect, incidental, special, or consequential damages arising from your use of our platform. 
                  Our total liability shall not exceed the amount you paid for our services in the past 12 months.
                </p>
              </CardContent>
            </Card>

            {/* Account Termination */}
            <Card className="border-0 bg-gradient-to-br from-gray-50 to-slate-100 dark:from-gray-900/20 dark:to-slate-800/20">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl font-bold flex items-center gap-3">
                  <AlertCircle className="h-6 w-6 text-gray-600" />
                  Account Termination
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-base sm:text-lg text-muted-foreground">
                    We reserve the right to terminate or suspend accounts that violate these terms. 
                    You may also terminate your account at any time by contacting our support team.
                  </p>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-base sm:text-lg text-muted-foreground">
                      Upon termination, your access to the platform will be revoked immediately
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gray-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-base sm:text-lg text-muted-foreground">
                      You may request a copy of your educational data within 30 days of termination
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Section */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent text-center">
              Legal Inquiries
            </h2>
            <Card className="border-0 bg-gradient-to-br from-indigo-50 to-blue-100 dark:from-indigo-900/20 dark:to-blue-800/20">
              <CardContent className="p-8">
                <p className="text-base sm:text-lg text-muted-foreground mb-6 text-center">
                  If you have questions about these Terms of Service or need legal clarification, please contact us:
                </p>
                <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                  <div className="flex items-center gap-3 p-4 bg-white/50 dark:bg-black/20 rounded-lg">
                    <Mail className="h-6 w-6 text-primary" />
                    <div>
                      <p className="font-semibold">Legal Email</p>
                      <a href="mailto:support@acehorizon.ai" className="text-primary hover:underline">
                        support@acehorizon.ai
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-white/50 dark:bg-black/20 rounded-lg">
                    <Phone className="h-6 w-6 text-primary" />
                    <div>
                      <p className="font-semibold">Legal Department</p>
                      <p className="text-muted-foreground">+91 9596413917</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </StaticPageLayout>
  );
}
